import { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardActionArea,
  CardContent,
  Tooltip,
  IconButton,
  TextField,
  FormHelperText,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper
} from '@mui/material';
import {
  ContentCut,
  PlayArrow,
  Info,
  InfoOutlined,
  Warning,
  Refresh,
  Science,
  Group,
  Category,
  Input,
  Check,
  Close,
  Download,
  QueuePlayNext
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { DraggableDialog } from '@/shared/components/DraggableDialog';
import { useSectionStyles } from '@/styles/utilities/hooks';
import api from '@/shared/services/api';
import { fmoClusterService } from '../services/fmoClusterService';
import { fmoGeneratorService } from '../services/fmoGeneratorService';
import { fmoSpecService } from '../services/fmoSpecService';
import { useFMOSpecs } from '../hooks/useFMOSpecs';
import { useFMOClusters } from '../hooks/useFMOClusters';
import { useConfigurationQueue } from '../hooks/useConfigurationQueue';
import { useFMOGeneration } from '../hooks/useFMOGeneration';
import FMOPanelAlert from '../components/FMOPanelAlert';
import FMOClusterConfiguration from '../components/FMOClusterConfiguration';
import FMOSpecConfiguration from '../components/FMOSpecConfiguration';
import FMOGenerateInputs from '../components/FMOGenerateInputs';
import FMOInputspecDetailsDialog from '../components/FMOInputspecDetailsDialog';
import FMOClusterDetailsDialog from '../components/FMOClusterDetailsDialog';
import ManualSetEditorDialog from '../components/ManualSetEditorDialog';
import ManualInputspecEditorDialog from '../components/ManualInputspecEditorDialog';
import InputspecDebugDialog from '../components/InputspecDebugDialog';

// Helper function to generate unique cluster name
const generateUniqueClusterName = (baseName, existingClusters) => {
  // Get all existing cluster names
  const existingNames = existingClusters.map(c => c.name).filter(Boolean);
  
  // If base name doesn't exist, use it
  if (!existingNames.includes(baseName)) {
    return baseName;
  }
  
  // Otherwise, append numbers until we find a unique name
  let counter = 2;
  let uniqueName = `${baseName}_${counter}`;
  
  while (existingNames.includes(uniqueName)) {
    counter++;
    uniqueName = `${baseName}_${counter}`;
  }
  
  return uniqueName;
};

const FMOInputGeneratorPanel = forwardRef(({ 
  selectedRows = [],
  preps = [],
  onFragmentationStart,
  previewMode = false
}, ref) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();
  const [showHelp, setShowHelp] = useState(false);

  // Use custom hooks for specs and clusters
  const {
    clusterspecs,
    pocketspecs,
    inputspecs,
    clusterspecsRef,
    selectedClusterspec,
    setSelectedClusterspec,
    selectedPocketspec,
    setSelectedPocketspec,
    selectedInputspec,
    setSelectedInputspec,
    selectedInputspecDetails,
    inputspecYaml,
    inputspecDialogOpen,
    setInputspecDialogOpen,
    handleInspectInputspec: inspectInputspec,
    resetSpecSelections,
    updateSelectionsFromConfig: updateSpecSelectionsFromConfig,
    refreshSpecs
  } = useFMOSpecs();
  
  const {
    loading,
    setLoading,
    loadingMessage,
    setLoadingMessage,
    clusters,
    setClusters,
    clustersWithTargets,
    setClustersWithTargets,
    availableClusters,
    clusterSelectionMode,
    setClusterSelectionMode,
    selectedCluster,
    setSelectedCluster,
    clusterName,
    setClusterName,
    clusterDialogOpen,
    setClusterDialogOpen,
    selectedClusterDetails,
    clusterNewName,
    setClusterNewName,
    clusterNewDescription,
    setClusterNewDescription,
    currentComparisonTarget,
    setCurrentComparisonTarget,
    seenRowsByTarget,
    setSeenRowsByTarget,
    fileAnalysis,
    handleInspectCluster: inspectCluster,
    handleDialogUpdate,
    handleUpdateCluster: updateCluster,
    resetClusterSelections,
    updateClusterSelectionsFromConfig: updateClusterSelectionsFromConfig,
    residueEditorOpen,
    setResidueEditorOpen,
    residueEditorMode,
    setResidueEditorMode,
    residueEditorSourceId,
    setResidueEditorSourceId
  } = useFMOClusters(selectedRows, clusterspecsRef);
  
  // Use configuration queue hook
  const {
    configurationQueue,
    setConfigurationQueue,
    activeConfigId,
    setActiveConfigId,
    handleAddNewConfiguration,
    handleSelectConfiguration,
    handleRemoveConfiguration: removeConfiguration,
    isDuplicateConfiguration,
    getUniqueConfigurations,
    resetConfigurationQueue
  } = useConfigurationQueue(
    clusterSelectionMode,
    selectedClusterspec,
    selectedCluster,
    clusterName,
    selectedPocketspec,
    selectedInputspec,
    updateClusterSelectionsFromConfig,
    updateSpecSelectionsFromConfig
  );
  
  // Function to refresh clusters only
  const refreshClusters = useCallback(async () => {
    try {
      // Force refresh with invalidated cache
      const { clusters: allClusters, clustersWithTargets: clustersWithTargetInfo } = 
        await fmoClusterService.fetchClustersWithTargets(clusterspecsRef.current, true);
      
      setClusters(allClusters);
      setClustersWithTargets(clustersWithTargetInfo);
      
    } catch (error) {
      console.error('Failed to refresh clusters:', error);
    }
  }, [clusterspecsRef, setClusters, setClustersWithTargets]);
  
  // Use FMO generation hook
  const {
    isProcessing,
    processingStatus,
    setProcessingStatus,
    generatedEntryIds,
    setGeneratedEntryIds,
    showDownload,
    setShowDownload,
    zipfileId,
    setZipfileId,
    handleGenerateInputFiles: generateInputFiles,
    handleDownloadZip: downloadZip,
    defaultZipfileName,
    resetGenerationState
  } = useFMOGeneration(
    selectedRows,
    fileAnalysis,
    configurationQueue,
    getUniqueConfigurations,
    clusterspecs,
    refreshClusters,
    onFragmentationStart
  );
  
  // Snackbar for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Manual inputspec editor dialog state
  const [inputspecEditorOpen, setInputspecEditorOpen] = useState(false);
  const [baseInputspecForEdit, setBaseInputspecForEdit] = useState(null);
  const [baseInputspecYaml, setBaseInputspecYaml] = useState('');
  
  // Debug dialog state (separate from main dialog for independent positioning)
  const [inputspecDebugOpen, setInputspecDebugOpen] = useState(false);
  const [inputspecDebugData, setInputspecDebugData] = useState({ overrides: {}, deletions: {} });
  
  // Reset cluster name when selection changes
  useEffect(() => {
    setClusterName(null); // Reset to use default
  }, [selectedRows]);
  
  // Note: fileAnalysis and availableClusters now come from useFMOClusters hook
  // Note: Configuration queue logic now comes from useConfigurationQueue hook
  
  // Use removeConfiguration directly from the hook (renamed to handleRemoveConfiguration for consistency)
  const handleRemoveConfiguration = removeConfiguration;
  
  // Expose reset method to parent
  useImperativeHandle(ref, () => ({
    reset: () => {
      resetClusterSelections();
      resetSpecSelections();
      resetConfigurationQueue();
      resetGenerationState();
    },
    toggleHelp: () => {
      setShowHelp(prev => !prev);
    }
  }), [resetClusterSelections, resetSpecSelections, resetConfigurationQueue, resetGenerationState]);
  // Wrapper functions for hook actions to handle events and snackbar
  const handleInspectCluster = async (clusterId, e) => {
    e.stopPropagation();
    try {
      await inspectCluster(clusterId);
    } catch (error) {
      setSnackbarMessage('Failed to load cluster details');
      setSnackbarOpen(true);
    }
  };
  
  const handleUpdateCluster = async () => {
    try {
      const result = await updateCluster();
      if (result) {
        setSnackbarMessage('Cluster updated successfully');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage('Failed to update cluster');
      setSnackbarOpen(true);
    }
  };
  
  // Wrapper function for hook action to handle event and snackbar
  const handleInspectInputspec = async (specId, e) => {
    e.stopPropagation();
    try {
      await inspectInputspec(specId);
    } catch (error) {
      setSnackbarMessage('Failed to load inputspec details');
      setSnackbarOpen(true);
    }
  };
  // Note: Specs and clusters are now fetched via hooks (useFMOSpecs and useFMOClusters)
  // Note: FMO generation logic now comes from useFMOGeneration hook
  
  // Wrapper functions for generation actions
  const handleGenerateInputFiles = async (zipfileName) => {
    await generateInputFiles(zipfileName, setSnackbarMessage, setSnackbarOpen);
  };
  
  const handleDownloadZip = async (zipfileId, zipfileName) => {
    await downloadZip(zipfileId, zipfileName, setSnackbarMessage, setSnackbarOpen);
  };
  
  // Handler for Create Now button - creates residue set immediately
  const handleCreateNow = async () => {
    try {
      if (!selectedClusterspec) {
        throw new Error('Please select a cutoff rule');
      }

      setLoading(true);
      setLoadingMessage('Creating residue set...');

      // Determine target
      const target = fileAnalysis.isSingleTarget ? fileAnalysis.targets[0] : fileAnalysis.targets[0];
      const targetFiles = selectedRows.filter(f => f.target === target);

      if (targetFiles.length === 0) {
        throw new Error('No files selected for target ' + target);
      }

      // Use the provided name or generate default (matching the UI logic)
      const finalClusterName = clusterName !== null ? clusterName : (() => {
        const spec = clusterspecs.find(s => s.id_ === selectedClusterspec);
        const today = new Date().toISOString().slice(2, 10).replace(/-/g, '').slice(0, 6);
        const batches = [...new Set(
          targetFiles
            .map(f => f.batch)
            .filter(Boolean)
            .filter(b => b !== '<none>')
        )];
        const batchPart = batches.length === 1 && batches[0]
          ? batches[0]
          : batches.length > 1
            ? 'mixed'
            : `${targetFiles.length}files`;
        return `${today}_${target}_${batchPart}_${spec?.name || 'cluster'}`;
      })();

      if (!finalClusterName?.trim()) {
        throw new Error('Please provide a name for the residue set');
      }

      // Create a group for these files
      const prepIds = targetFiles.map(f => f.id_ || f.id);
      const groupInfo = await fmoGeneratorService.createGroup(prepIds, target);
      const groupId = groupInfo.id_;

      // Ensure the name is unique
      const uniqueClusterName = generateUniqueClusterName(finalClusterName.trim(), clustersWithTargets);

      // Create cluster with cutoff rule
      const clusterId = await fmoGeneratorService.createCluster({
        name: uniqueClusterName,
        clusterspecId: selectedClusterspec,
        groupId: groupId,
        target: target
      });

      // Refresh clusters list
      await refreshClusters();

      // Switch to the newly created cluster
      setClusterSelectionMode('existing');
      setSelectedCluster(clusterId);
      setSelectedClusterspec('');
      setClusterName(null);

      setSnackbarMessage(`Residue set "${uniqueClusterName}" created successfully`);
      setSnackbarOpen(true);

    } catch (error) {
      console.error('Failed to create residue set:', error);
      setSnackbarMessage('Failed to create residue set: ' + error.message);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Handler for Create Modified Copy button - opens editor for existing residue set
  const handleOpenManualSetEditor = async () => {
    try {
      // Only handle existing cluster selection now
      if (clusterSelectionMode === 'existing' && selectedCluster) {
        // Directly open manual editor for existing cluster
        setResidueEditorOpen(true);
        setResidueEditorMode('from-existing');
        setResidueEditorSourceId(selectedCluster);
      } else {
        throw new Error('Please select an existing residue set first');
      }
    } catch (error) {
      console.error('Failed to open residue set editor:', error);
      setSnackbarMessage('Failed to open residue set editor: ' + error.message);
      setSnackbarOpen(true);
    }
  };
  
  // Handler for opening custom inputspec editor
  const handleOpenCustomInputspecEditor = async () => {
    try {
      // Get the currently selected inputspec
      const baseSpec = inputspecs.find(spec => spec.id_ === selectedInputspec);
      if (!baseSpec) {
        setSnackbarMessage('No inputspec selected');
        setSnackbarOpen(true);
        return;
      }
      
      // Fetch the YAML for the base inputspec
      const { yaml } = await fmoSpecService.getInputspecDetails(selectedInputspec);
      
      setBaseInputspecForEdit(baseSpec);
      setBaseInputspecYaml(yaml);
      setInputspecEditorOpen(true);
    } catch (error) {
      console.error('Failed to open inputspec editor:', error);
      setSnackbarMessage('Failed to load inputspec details');
      setSnackbarOpen(true);
    }
  };
  
  // Handler for saving custom inputspec
  const handleSaveCustomInputspec = async (inputspecData) => {
    try {
      setLoading(true);
      setLoadingMessage('Creating custom configuration...');
      
      // Create the inputspec via API
      let response = await api.post('/inputspecs', [inputspecData]);
      console.log('Initial inputspec response:', response);
      
      // Poll if 202 Accepted
      let pollCount = 0;
      const maxPolls = 60;
      const pollInterval = 1000;
      
      while (response.status === 202 && pollCount < maxPolls) {
        pollCount++;
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        response = await api.post('/inputspecs', [inputspecData]);
        console.log(`Polling attempt ${pollCount}:`, response);
      }
      
      if (response.status === 202) {
        throw new Error('Configuration creation timeout');
      }
      
      // Handle different response structures
      let createdSpec = null;
      if (response.data) {
        // Check for workflow response
        if (response.data.workflow && response.data.workflow.result && response.data.workflow.result.items) {
          createdSpec = response.data.workflow.result.items[0];
        } 
        // Check for array response
        else if (Array.isArray(response.data) && response.data.length > 0) {
          createdSpec = response.data[0];
        } 
        // Check for direct object response
        else if (response.data.id_) {
          createdSpec = response.data;
        }
      }
      
      console.log('Created inputspec:', createdSpec);
      
      if (createdSpec) {
        // Refresh inputspecs list and get the updated specs
        const refreshedSpecs = await refreshSpecs();
        
        // Find and select the newly created inputspec from the refreshed list
        // The new spec should have the name we provided
        const newSpec = refreshedSpecs.inputspecs.find(spec => spec.name === inputspecData.name);
        if (newSpec) {
          setSelectedInputspec(newSpec.id_);
        }
        
        setSnackbarMessage(`Custom configuration "${inputspecData.name}" created successfully`);
        setSnackbarOpen(true);
        
        // Close the dialog after successful save
        setInputspecEditorOpen(false);
      } else {
        throw new Error('Invalid response structure from API');
      }
    } catch (error) {
      console.error('Failed to create custom inputspec:', error);
      setSnackbarMessage('Failed to create custom configuration: ' + error.message);
      setSnackbarOpen(true);
      // Close dialog on error so user isn't stuck
      setInputspecEditorOpen(false);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };
  
  // Handler for creating manual residue set from editor
  const handleCreateManualSet = async (params) => {
    try {
      setLoading(true);
      setLoadingMessage('Creating manual residue set...');
      
      // Get target from params or fileAnalysis
      const target = params.target || (fileAnalysis.isSingleTarget ? fileAnalysis.targets[0] : fileAnalysis.targets[0]);
      const targetFiles = selectedRows.filter(f => f.target === target);
      
      // Create a group for these files
      const prepIds = targetFiles.map(f => f.id_ || f.id);
      const groupInfo = await fmoGeneratorService.createGroup(prepIds, target);
      const groupId = groupInfo.id_;
      
      // Create manual cluster
      const clusterId = await fmoClusterService.createManualCluster({
        name: params.name,
        description: params.description || `Manual selection from ${params.sourceName}`,
        target: target,
        groupId: groupId,
        sourceClusterId: params.sourceClusterId,
        residues: params.residues
      });
      
      // Refresh clusters list
      await refreshClusters();
      
      // Show success message
      setSnackbarMessage(`Manual residue set "${params.name}" created successfully`);
      setSnackbarOpen(true);
      
      // Select the new cluster
      setSelectedCluster(clusterId);
      setClusterSelectionMode('existing');
      
      return clusterId;
    } catch (error) {
      console.error('Failed to create manual residue set:', error);
      setSnackbarMessage('Failed to create manual residue set: ' + error.message);
      setSnackbarOpen(true);
      throw error;
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };
  
  // Refresh data
  const handleRefresh = () => {
    window.location.reload(); // Simple refresh for now
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Help Dialog - Draggable */}
      <DraggableDialog
        open={showHelp}
        onClose={() => setShowHelp(false)}
        title="FMO Input Generator Help"
        variant="help"
        maxWidth="md"
        fullWidth
      >
        <Box sx={{
          '& h3': { fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, mt: theme.spacing(2), mb: theme.spacing(1) },
          '& h3:first-of-type': { mt: 0 },
          '& ul': { pl: theme.spacing(2), my: theme.spacing(0.5) },
          '& li': { fontSize: theme.typography.fontSize.xs, mb: theme.spacing(0.5) },
          '& code': {
            backgroundColor: theme.palette.grey[200],
            px: theme.spacing(0.5),
            py: theme.spacing(0.2),
            borderRadius: theme.spacing(0.5),
            fontSize: theme.typography.fontSize.xs,
            fontFamily: 'monospace'
          },
          '& p': { fontSize: theme.typography.fontSize.xs, my: theme.spacing(0.5) },
          '& i.material-icons': {
            fontSize: `${theme.dimensions.icon.small}px !important`,
            verticalAlign: 'middle',
            marginRight: theme.spacing(0.25)
          }
        }}>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
{`### 1. Select Structures for FMO Generation

- Select prepared PDB files from the main table using:
  - \`Click\` — Select single file
  - \`Shift\`+\`Click\` — Select range of files
  - \`Ctrl\`/\`Cmd\`+\`Click\` — Add/remove individual files
  - \`Ctrl\`/\`Cmd\`+\`A\` — Select all visible files
- Files must have target information for FMO processing
- Multiple targets will be processed separately

### 2. Configure Residue Set

Choose one of the following approaches:

**Option 1: Use Cutoff or Existing Residue Set**
- Select a distance cutoff (e.g., 5Å, 10Å) to automatically define residues
- Or choose from existing residue sets for your target
- Click <i class="material-icons">info</i> to view residue set details

**Option 2: Modify Existing Residue Set**
- Select an existing residue set as a starting point
- Click "Manual Set Editor" to remove specific residues
- Creates a custom manual residue set based on your modifications

**Option 3: Create New Residue Set**
- Start with an empty set
- Use "Manual Set Editor" to add specific residues
- Full control over which residues to include

### 3. Configure FMO Settings

Set the calculation parameters:

**Residue Set:**
- Defines which protein residues are included in the calculation
- Can be distance-based (cutoff) or manually defined

**Terminal Capping:**
- Options for capping terminal residues
- Available options: \`Hydrogen capping\`, \`Methyl capping\`

**Configuration:**
- Sets calculation parameters (basis set, method, etc.)
- Examples: \`FMO 6-31G**\`, \`FMO/HI-VQE\`
- Defines fragmentation scheme and calculation level

### 4. Configure Combinations

- Review your selected settings in the configuration card
- Click "Add to Queue" to add the combination
- Multiple combinations can be queued for batch processing
- Each combination will generate separate input files
- Remove unwanted combinations with the <i class="material-icons">close</i> button

### 5. Manual Configuration (Advanced)

**Manual Set Editor:**
- Define custom residue sets by complex IDs
- Enter complex IDs (one per line) to specify exact structures
- Modify existing sets by removing specific residues
- Useful for creating focused analysis groups

**Manual Configuration Editor:**
- Create custom calculation configurations
- Define parameters not available in presets
- Requires knowledge of FMO calculation syntax

### 6. Generate Input Files

- Review the configuration queue showing all combinations
- Click "Generate Input Files" to start the process
- Progress will be shown for each combination
- Large file sets may take several minutes to process
- Once complete, a download button will appear
- Click the download button to save the ZIP file
- If you miss the download, files can be accessed from the Inputs tab
- The ZIP contains:
  - Input files for each structure
  - Residue set information
  - Configuration details
  - Ready for submission to FMO calculation software`}
          </ReactMarkdown>
        </Box>
      </DraggableDialog>

      {/* Selection Info - Matching other panels' layout */}
      <FMOPanelAlert
        selectedRows={selectedRows}
        availableClusters={availableClusters}
      />
      
      {/* Scrollable Content Container */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        px: theme.spacing(2),
        pb: theme.spacing(2)
      }}>
        {/* Cluster Configuration Component */}
        <FMOClusterConfiguration
          loading={loading}
          selectedRows={selectedRows}
          fileAnalysis={fileAnalysis}
          clusterspecs={clusterspecs}
          clustersWithTargets={clustersWithTargets}
          clusterSelectionMode={clusterSelectionMode}
          selectedClusterspec={selectedClusterspec}
          selectedCluster={selectedCluster}
          clusterName={clusterName}
          configurationQueue={configurationQueue}
          activeConfigId={activeConfigId}
          onSelectionModeChange={setClusterSelectionMode}
          onClusterspecSelect={setSelectedClusterspec}
          onClusterSelect={setSelectedCluster}
          onClusterNameChange={setClusterName}
          onInspectCluster={handleInspectCluster}
          clusterDialogOpen={clusterDialogOpen}
          onDialogUpdate={handleDialogUpdate}
          onOpenResidueEditor={handleOpenManualSetEditor}
          onCreateNow={handleCreateNow}
        />
      
        {/* Spec Configuration Component */}
        <FMOSpecConfiguration
          pocketspecs={pocketspecs}
          inputspecs={inputspecs}
          selectedPocketspec={selectedPocketspec}
          selectedInputspec={selectedInputspec}
          onPocketspecSelect={setSelectedPocketspec}
          onInputspecSelect={setSelectedInputspec}
          onInspectInputspec={handleInspectInputspec}
          onCreateCustomInputspec={handleOpenCustomInputspecEditor}
        />
      
        {/* FMO Generate Inputs Component */}
        <FMOGenerateInputs
          configurationQueue={configurationQueue}
          clusterspecs={clusterspecs}
          pocketspecs={pocketspecs}
          inputspecs={inputspecs}
          availableClusters={availableClusters}
          selectedRows={selectedRows}
          fileAnalysis={fileAnalysis}
          isProcessing={isProcessing}
          processingStatus={processingStatus}
          showDownload={showDownload}
          generatedEntryIds={generatedEntryIds}
          zipfileId={zipfileId}
          defaultZipfileName={defaultZipfileName}
          onSelectConfiguration={handleSelectConfiguration}
          onRemoveConfiguration={handleRemoveConfiguration}
          onAddNewConfiguration={handleAddNewConfiguration}
          isDuplicateConfiguration={isDuplicateConfiguration}
          getUniqueConfigurations={getUniqueConfigurations}
          onGenerateInputFiles={handleGenerateInputFiles}
          onDownloadZip={handleDownloadZip}
          onClearDownload={resetGenerationState}
        />
      </Box>
      
      {/* Cluster Details Dialog */}
      <FMOClusterDetailsDialog
        open={clusterDialogOpen}
        onClose={() => {
          setClusterDialogOpen(false);
          setCurrentComparisonTarget(null);
        }}
        clusterDetails={selectedClusterDetails}
        clusterNewName={clusterNewName}
        clusterNewDescription={clusterNewDescription}
        onNameChange={setClusterNewName}
        onDescriptionChange={setClusterNewDescription}
        onUpdateCluster={updateCluster}
        clusterspecs={clusterspecs}
        currentComparisonTarget={currentComparisonTarget}
        seenRowsByTarget={seenRowsByTarget}
        onSeenRowsUpdate={setSeenRowsByTarget}
      />
      
      {/* Inputspec Details Dialog */}
      <FMOInputspecDetailsDialog
        open={inputspecDialogOpen}
        onClose={() => setInputspecDialogOpen(false)}
        inputspecDetails={selectedInputspecDetails}
        inputspecYaml={inputspecYaml}
      />
      
      {/* Manual Set Editor Dialog */}
      <ManualSetEditorDialog
        open={residueEditorOpen}
        onClose={() => {
          setResidueEditorOpen(false);
          setResidueEditorMode('');
          setResidueEditorSourceId(null);
        }}
        sourceMode={residueEditorMode}
        sourceClusterId={residueEditorSourceId}
        clustersWithTargets={clustersWithTargets}
        fileAnalysis={fileAnalysis}
        onCreateManualSet={handleCreateManualSet}
        selectedRows={selectedRows}
      />
      
      {/* Manual Inputspec Editor Dialog */}
      <ManualInputspecEditorDialog
        open={inputspecEditorOpen}
        onClose={() => setInputspecEditorOpen(false)}
        baseInputspec={baseInputspecForEdit}
        baseYaml={baseInputspecYaml}
        onSave={handleSaveCustomInputspec}
        onDebugOpen={(data) => {
          setInputspecDebugData(data);
          setInputspecDebugOpen(true);
        }}
        onDebugDataChange={(data) => {
          // Auto-update debug data when YAML changes
          setInputspecDebugData(data);
        }}
      />
      
      {/* Inputspec Debug Dialog (separate for independent positioning) */}
      <InputspecDebugDialog
        open={inputspecDebugOpen}
        onClose={() => setInputspecDebugOpen(false)}
        overrides={inputspecDebugData.overrides || {}}
        deletions={inputspecDebugData.deletions || {}}
        groupOrder={{}}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
});

FMOInputGeneratorPanel.displayName = 'FMOInputGeneratorPanel';

export default FMOInputGeneratorPanel;